import { useMemo } from 'react';
import { restoreLegacyAiDraftTags } from '@/utils/approval/aiDraftSection';
import { isContractApprovalType } from '@/utils/approval/contractIds';
import { openDownloadUrlInNewTab } from '@/utils/approval/resolveDownloadUrl';
import { isEmptyContent } from '@/utils/editor';
import ContentHtmlRender from './ContentHtmlRender';
import { renderContentExtra } from './slots';
import { InlineImageBox, InlineImagesGrid } from './styles';
import type { ApprovalViewAttachment } from './types';

type ViewContentProps = {
  apvType: number;
  documentId: string;
  content: string | null;
  attributes: Record<string, unknown> | null;
  /** 첨부 이미지 본문 하단 자동 노출 옵션 (응답 `showImagesBelowContent`) */
  showImagesBelowContent: boolean;
  /** 본문 하단 이미지 그리드 + (옵션) 슬롯 컴포넌트에 전달할 첨부 배열 */
  attachments: ApprovalViewAttachment[];
};

/**
 * 조회 본문 분자 — 유형별 슬롯(1번 유형은 null) + HTML 본문 + 하단 이미지 그리드.
 * 슬롯이 본문보다 앞 — 레거시 조회도 양식 영역(appr_plus_form)을 내용 위에 둠.
 * 첨부 영역(AttachmentsReadOnly)은 형제 영역이라 한 단계 위에서 별도 마운트.
 */
const ViewContent = ({
  apvType,
  documentId,
  content,
  attributes,
  showImagesBelowContent,
  attachments,
}: ViewContentProps) => {
  const Extra = useMemo(() => renderContentExtra(apvType), [apvType]);
  const html = useMemo(
    () =>
      content && isContractApprovalType(apvType)
        ? restoreLegacyAiDraftTags(content)
        : content,
    [apvType, content],
  );

  const imageAttachments = useMemo(
    () =>
      attachments.filter(
        (a) => !a.isDeleted && a.mimeType === 'image' && a.url,
      ),
    [attachments],
  );

  return (
    <>
      {Extra ? <Extra data={{ attributes, documentId, apvType }} /> : null}

      {/* 빈 에디터 마크업만 저장된 기존 문서는 빈 줄이 생겨 본문 없음으로 취급 */}
      <ContentHtmlRender html={html && !isEmptyContent(html) ? html : null} />

      {showImagesBelowContent && imageAttachments.length > 0 && (
        <InlineImagesGrid>
          {imageAttachments.map((img) => {
            // 원본명은 fileDescription. 비면 fileName 폴백
            const name = img.fileDescription?.trim() || img.fileName;
            return (
              <InlineImageBox
                key={img.fileId}
                href={img.url}
                target="_blank"
                rel="noopener noreferrer"
                // 응답의 url 은 짧게 만료되므로 클릭 시점에 재발급받아 연다 (PORTAL-13977).
                // fileUuid 없는 구 첨부는 재발급할 것이 없어 기본 링크 동작에 맡긴다.
                onClick={(e) => {
                  if (!img.fileUuid) return;
                  e.preventDefault();
                  openDownloadUrlInNewTab(img.fileUuid, img.url);
                }}
                aria-label={name}
              >
                <img src={img.url} alt={name} loading="lazy" />
              </InlineImageBox>
            );
          })}
        </InlineImagesGrid>
      )}
    </>
  );
};

export default ViewContent;
